// src/services/fulfillmentAPI.ts
// Fulfillment API service for qmLEO
// Handles converting purchase line items into inventory instances
// Calls /fulfillment/* endpoints

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';

const fulfillmentLogger = logger.createServiceLogger('FulfillmentAPI');

// Request/Response types
export interface SerializedItemInput {
  serialNumber: string;
  condition?: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'NEEDS_REPAIR';
  location?: string;
  subLocation?: string;
  notes?: string;
  photos?: string[];
  bodyArmor?: {
    ballisticLevel: string;
    size: string;
    expirationDate: string;
  };
}

export interface CreateSerializedRequest {
  purchaseId: string;
  lineItemId: string;
  productId: string;
  items: SerializedItemInput[];
  categoryName?: string;
  subcategoryName?: string;
  categoryId?: string;
  subcategoryId?: string;
}

export interface CreateSerializedResponse {
  inventoryIds: string[];
  historyIds: string[];
  lineItem: {
    lineItemId: string;
    allocatedQuantity: number;
    totalQuantity: number;
  };
}

export interface CreateBulkStockRequest {
  purchaseId: string;
  lineItemId: string;
  productId: string;
  quantity: number;
  location: string;
  subLocation?: string;
  minStockLevel?: number;
  lotNumber?: string;
  expirationDate?: string;
  notes?: string;
  categoryName?: string;
  subcategoryName?: string;
  categoryId?: string;
  subcategoryId?: string;
}

export interface CreateBulkStockResponse {
  stockId: string;
  totalQuantity: number;
  availableQuantity: number;
  lineItem: {
    lineItemId: string;
    allocatedQuantity: number;
    totalQuantity: number;
  };
}

export interface RestockBulkRequest {
  purchaseId: string;
  lineItemId: string;
  stockId: string;
  quantityToAdd: number;
}

export interface RestockBulkResponse {
  stockId: string;
  previousQuantity: number;
  addedQuantity: number;
  newQuantity: number;
  lineItem: {
    lineItemId: string;
    allocatedQuantity: number;
    totalQuantity: number;
  };
}

// API response wrapper
interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class FulfillmentAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('FulfillmentAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('FulfillmentAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    const headers = new Headers(options.headers || {});
    if (options.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('FulfillmentAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const data = await response.json();

      if (!response.ok) {
        fulfillmentLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error: data.error || {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}`,
          },
        };
      }

      return data as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      fulfillmentLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  /**
   * Create serialized inventory instances from a purchase line item
   */
  async createSerializedInstances(request: CreateSerializedRequest): Promise<CreateSerializedResponse> {
    fulfillmentLogger.info('Creating serialized instances', { 
      purchaseId: request.purchaseId,
      lineItemId: request.lineItemId,
      productId: request.productId,
      itemCount: request.items.length
    });

    const response = await this.makeRequest<CreateSerializedResponse>('/fulfillment/serialized', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create serialized instances');
    }

    fulfillmentLogger.info('Serialized instances created', { 
      inventoryIds: response.data.inventoryIds 
    });
    return response.data;
  }

  /**
   * Create initial bulk stock from a purchase line item
   */
  async createBulkStock(request: CreateBulkStockRequest): Promise<CreateBulkStockResponse> {
    fulfillmentLogger.info('Creating bulk stock', { 
      purchaseId: request.purchaseId,
      lineItemId: request.lineItemId,
      productId: request.productId,
      quantity: request.quantity,
      location: request.location
    });

    const response = await this.makeRequest<CreateBulkStockResponse>('/fulfillment/bulk-create', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create bulk stock');
    }

    fulfillmentLogger.info('Bulk stock created', { 
      stockId: response.data.stockId,
      quantity: response.data.totalQuantity
    });
    return response.data;
  }

  /**
   * Add quantity to existing bulk stock from a purchase line item
   */
  async restockBulkItem(request: RestockBulkRequest): Promise<RestockBulkResponse> {
    fulfillmentLogger.info('Restocking bulk item', { 
      purchaseId: request.purchaseId,
      lineItemId: request.lineItemId,
      stockId: request.stockId,
      quantityToAdd: request.quantityToAdd
    });

    const response = await this.makeRequest<RestockBulkResponse>('/fulfillment/bulk-restock', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to restock bulk item');
    }

    fulfillmentLogger.info('Bulk item restocked', { 
      stockId: response.data.stockId,
      previousQuantity: response.data.previousQuantity,
      newQuantity: response.data.newQuantity
    });
    return response.data;
  }
}

// Export singleton instance
export const fulfillmentAPI = new FulfillmentAPI();
